import { AxiosResponse } from "axios";
import { User } from "../types/auth";
import api from "../utils/apiClient";

interface LoginUrlResponse {
  loginUrl: string;
}

interface ExchangeResponse {
  success: boolean;
  user: User;
  returnUrl: string;
}

export const authService = {
  /**
   * Get login URL and redirect to 3PM Auth IdP
   * @param returnUrl - URL to redirect to after authentication
   */
  async login(returnUrl: string = window.location.pathname): Promise<void> {
    try {
      const response: AxiosResponse<LoginUrlResponse> = await api.get(
        `/auth/login?returnUrl=${encodeURIComponent(returnUrl)}`
      );
      // Redirect to IdP login URL
      window.location.href = response.data.loginUrl;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || "Failed to initiate login");
    }
  },

  /**
   * Handle OAuth callback - exchange GUID for session
   * @param guid - GUID token from IdP callback
   * @param returnUrl - URL to redirect to after exchange
   * @returns returnUrl to redirect to
   */
  async handleCallback(guid: string, returnUrl: string = "/"): Promise<string> {
    try {
      const response: AxiosResponse<ExchangeResponse> = await api.post(
        "/auth/exchange",
        { guid, returnUrl }
      );

      if (response.data.success) {
        // Store user info in localStorage for quick access (cookie handles auth)
        localStorage.setItem("user", JSON.stringify(response.data.user));
        return response.data.returnUrl;
      } else {
        throw new Error("Authentication failed");
      }
    } catch (error: any) {
      const errorMsg = error.response?.data?.detail || "Authentication failed";
      throw new Error(errorMsg);
    }
  },

  /**
   * Get current user from backend (verifies session cookie)
   */
  async getCurrentUser(): Promise<User> {
    try {
      const response: AxiosResponse<User> = await api.get("/auth/me");
      // Update localStorage with fresh user data
      localStorage.setItem("user", JSON.stringify(response.data));
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.detail || "Failed to get user info"
      );
    }
  },

  /**
   * Logout user (clears session cookie)
   */
  async logout(): Promise<void> {
    try {
      await api.post("/auth/logout");
    } catch (error) {
      // Continue with logout even if backend call fails
      console.warn("Backend logout failed, continuing with local logout");
    } finally {
      // Clear local user data
      localStorage.removeItem("user");
    }
  },

  /**
   * Check if user is authenticated (checks localStorage for user)
   * Note: Actual auth is handled by session cookie, this is just for UI state
   */
  isAuthenticated(): boolean {
    const userStr = localStorage.getItem("user");
    return !!userStr;
  },

  /**
   * Get stored user from localStorage
   */
  getStoredUser(): User | null {
    const userStr = localStorage.getItem("user");
    if (userStr) {
      try {
        return JSON.parse(userStr);
      } catch {
        return null;
      }
    }
    return null;
  },

  /**
   * Login with email and password credentials
   * @param email - User email
   * @param password - User password
   */
  async loginWithCredentials(email: string, password: string): Promise<User> {
    try {
      const response: AxiosResponse<{ success: boolean; user: User }> = await api.post(
        "/auth/login-credentials",
        { email, password }
      );

      if (response.data.success && response.data.user) {
        // Store user info in localStorage
        localStorage.setItem("user", JSON.stringify(response.data.user));
        return response.data.user;
      } else {
        throw new Error("Login failed");
      }
    } catch (error: any) {
      const errorMsg =
        error.response?.data?.detail || error.message || "Login failed";
      throw new Error(errorMsg);
    }
  },

  /**
   * Register a new user with email and password
   * @param email - User email
   * @param username - Username
   * @param password - User password
   * @param fullName - Optional full name
   */
  async register(
    email: string,
    username: string,
    password: string,
    fullName?: string
  ): Promise<User> {
    try {
      const response: AxiosResponse<{ success: boolean; user: User }> = await api.post(
        "/auth/register",
        { email, username, password, full_name: fullName }
      );

      if (response.data.success && response.data.user) {
        // Store user info in localStorage
        localStorage.setItem("user", JSON.stringify(response.data.user));
        return response.data.user;
      } else {
        throw new Error("Registration failed");
      }
    } catch (error: any) {
      const errorMsg =
        error.response?.data?.detail || error.message || "Registration failed";
      throw new Error(errorMsg);
    }
  },
};

export default authService;
